// Klientský engine generování termínů – zrcadlí backendový TermDateGenerator (autoritativní výpočet běží
// na serveru). Slouží jen pro živý náhled v dialogu. Pracuje s lokálními půlnočními daty (Date).

export type TermGenMode = 'recurrence' | 'calendar';
export type TermSpan = 'includeEnds' | 'startsOnly';
export type TermRecurrence = 'chain' | 'days' | 'weeks';

export interface TermGenInput {
  mode: TermGenMode;
  /** Délka termínu v nocích (konec = začátek + nocí). */
  nights: number;
  from: Date | null;
  to: Date | null;
  span: TermSpan;
  recurrence: TermRecurrence;
  everyDays: number;
  everyWeeks: number;
  /** Dny v týdnu jako getDay(): 0 = neděle … 6 = sobota. */
  daysOfWeek: number[];
  calendarStarts: Date[];
}

export interface GeneratedTerm {
  start: Date;
  end: Date;
}

export interface TermGenResult {
  terms: GeneratedTerm[];
  truncated: boolean;
}

/** Tvrdá pojistka – engine nikdy nevygeneruje víc termínů (shodné s backendem). */
export const GEN_MAX = 3000;
/** Měkký limit – nad ním si generování vyžaduje potvrzení („Povolit"). */
export const GEN_WARN = 200;
/** Maximální počet řádků vykreslených v náhledu, než se prostředek sbalí. */
export const GEN_PREVIEW_MAX = 500;

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function isoOf(date: Date): string {
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p2(date.getMonth() + 1)}-${p2(date.getDate())}`;
}

export function parseIso(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function mondayIndex(date: Date): number {
  return (date.getDay() + 6) % 7; // Po=0 … Ne=6
}

function startOfWeekMonday(date: Date): Date {
  return addDays(date, -mondayIndex(date));
}

export function generateTerms(input: TermGenInput): TermGenResult {
  const nights = Math.max(0, input.nights);
  const terms: GeneratedTerm[] = [];
  const push = (start: Date) => terms.push({ start, end: addDays(start, nights) });

  if (input.mode === 'calendar') {
    const unique = [...new Set(input.calendarStarts.map(isoOf))].sort();
    unique.forEach((iso) => push(parseIso(iso)));
    return { terms, truncated: false };
  }

  if (!input.from || !input.to || input.to < input.from) {
    return { terms, truncated: false };
  }

  const to = input.to;
  const fits = (start: Date) =>
    input.span === 'includeEnds' ? addDays(start, nights) <= to : start <= to;

  let truncated = false;

  if (input.recurrence === 'chain') {
    let cursor = new Date(input.from);
    while (fits(cursor)) {
      push(cursor);
      if (terms.length >= GEN_MAX) {
        truncated = true;
        break;
      }
      cursor = addDays(cursor, Math.max(1, nights));
    }
  } else if (input.recurrence === 'days') {
    const step = Math.max(1, input.everyDays);
    for (let cur = new Date(input.from); cur <= to; cur = addDays(cur, step)) {
      if (!fits(cur)) continue;
      push(cur);
      if (terms.length >= GEN_MAX) {
        truncated = true;
        break;
      }
    }
  } else {
    const step = Math.max(1, input.everyWeeks);
    const days = new Set(input.daysOfWeek);
    const anchor = startOfWeekMonday(input.from);
    for (let cur = new Date(input.from); cur <= to; cur = addDays(cur, 1)) {
      if (!days.has(cur.getDay()) || !fits(cur)) continue;
      const weekIndex = Math.round(
        (startOfWeekMonday(cur).getTime() - anchor.getTime()) / 6048e5 // /(7*24*3600*1000)
      );
      if (weekIndex % step !== 0) continue;
      push(cur);
      if (terms.length >= GEN_MAX) {
        truncated = true;
        break;
      }
    }
  }

  return { terms, truncated };
}
